import { homePath } from "@/lib/roles";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Gamepad2, AlertCircle, Rocket, GraduationCap } from "lucide-react";
import API, { formatApiError } from "@/lib/api";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

export default function AuthPage({ initialTab = "login" }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regRole, setRegRole] = useState("student");
  const [regClassCode, setRegClassCode] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(loginEmail, loginPassword);
      navigate(homePath(user.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimTeacher = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/auth/claim-teacher", { email: loginEmail, password: loginPassword });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("refresh_token", data.refresh_token);
      window.location.href = "/teacher";
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (regPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const user = await register(regEmail, regPassword, regName, regRole, regClassCode || undefined);
      navigate(homePath(user.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Gamepad2 className="w-10 h-10 text-cyber" />
            <h1 className="font-heading text-4xl font-bold tracking-tight text-white">
              Math<span className="text-cyber">Blaster</span>
            </h1>
          </div>
          <p className="text-white/50 font-body text-base">
            Solve. Earn. Blast asteroids.
          </p>
        </div>

        <div className="glass-surface rounded-xl p-6">
          {/* Google Sign-In */}
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 px-4 py-3 mb-5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors duration-200 font-body text-sm text-white"
          >
            <GoogleIcon />
            Sign in with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/30 font-body">or continue with email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <Tabs defaultValue={initialTab} onValueChange={() => setError("")}>
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-white/5">
              <TabsTrigger
                value="login"
                data-testid="auth-login-tab"
                className="font-body data-[state=active]:bg-cyber/10 data-[state=active]:text-cyber"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger
                value="register"
                data-testid="auth-register-tab"
                className="font-body data-[state=active]:bg-cyber/10 data-[state=active]:text-cyber"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-magenta/10 border border-magenta/20 mb-4" data-testid="auth-error">
                <AlertCircle className="w-4 h-4 text-magenta shrink-0" />
                <span className="text-sm text-magenta font-body">{error}</span>
              </div>
            )}

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-white/70 font-body text-sm">Email</Label>
                  <Input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="login-email-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 font-body text-sm">Password</Label>
                  <Input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    data-testid="login-password-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full btn-cyber rounded-full h-11 font-heading text-sm tracking-wider"
                >
                  {loading ? "AUTHENTICATING..." : "LAUNCH SESSION"}
                </Button>
                <button
                  type="button"
                  onClick={handleClaimTeacher}
                  disabled={loading || !loginEmail || !loginPassword}
                  data-testid="claim-teacher-btn"
                  className="w-full mt-2 text-xs text-white/30 hover:text-neon font-body transition-colors duration-200 disabled:opacity-20"
                >
                  Restore teacher access
                </button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-white/70 font-body text-sm">Name</Label>
                  <Input
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="register-name-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 font-body text-sm">Email</Label>
                  <Input
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="register-email-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 font-body text-sm">Password</Label>
                  <Input
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                    data-testid="register-password-input"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/70 font-body text-sm">I am a</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRegRole("student")}
                      data-testid="register-role-student"
                      className={`flex items-center gap-2 p-3 rounded-lg border font-body text-sm transition-colors duration-200 ${
                        regRole === "student"
                          ? "border-cyber bg-cyber/10 text-cyber"
                          : "border-white/10 text-white/50 hover:border-white/20"
                      }`}
                    >
                      <Rocket className="w-4 h-4" />
                      Student
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegRole("teacher")}
                      data-testid="register-role-teacher"
                      className={`flex items-center gap-2 p-3 rounded-lg border font-body text-sm transition-colors duration-200 ${
                        regRole === "teacher"
                          ? "border-neon bg-neon/10 text-neon"
                          : "border-white/10 text-white/50 hover:border-white/20"
                      }`}
                    >
                      <GraduationCap className="w-4 h-4" />
                      Teacher
                    </button>
                  </div>
                </div>
                {regRole === "student" && (
                  <div className="space-y-2">
                    <Label className="text-white/70 font-body text-sm">Class Code <span className="text-white/30">(from your teacher)</span></Label>
                    <Input
                      value={regClassCode}
                      onChange={(e) => setRegClassCode(e.target.value.toUpperCase())}
                      placeholder="e.g. AB3K-7X2M"
                      data-testid="register-class-code-input"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-cyber focus:ring-cyber/30 font-mono tracking-wider"
                    />
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="w-full btn-cyber rounded-full h-11 font-heading text-sm tracking-wider"
                >
                  {loading ? "CREATING ACCOUNT..." : "CREATE PILOT"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
